import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  computed,
  effect,
  inject,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  ModalBodyComponent,
  ModalComponent,
  ModalFooterAction,
  ModalFooterComponent,
  ModalHeaderComponent,
} from '@groundworks/ngx-groundworks-ui';
import { PpjPaymentResponse, RecordPpjPaymentDto, UpdatePpjPaymentDto } from '../../sdk/sdk';
import { PpjService } from '../../services/ppj.service';
import { commissionErrorMessage } from '../../utils/commission-error-message';
import { formatUtcIsoDate, parseLocalIsoDate } from '../../utils/iso-date';
import { maxTwoDecimalPlaces } from '../../utils/form-validators';
import { dollarsFromPercentOfBasis, percentFromDollarsOfBasis } from '../../utils/ppj-rules';

@Component({
  selector: 'app-ppj-foreman-bonus-modal',
  imports: [
    ReactiveFormsModule,
    ModalComponent,
    ModalHeaderComponent,
    ModalBodyComponent,
    ModalFooterComponent,
  ],
  templateUrl: './ppj-foreman-bonus-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PpjForemanBonusModalComponent {
  private readonly formBuilder = inject(FormBuilder);
  private readonly ppjService = inject(PpjService);
  private readonly destroyRef = inject(DestroyRef);

  readonly projectId = signal('');
  readonly userId = signal('');
  readonly bonusPot = signal<number | null>(null);
  readonly payment = signal<PpjPaymentResponse | null>(null);
  /** The row's resolved percentage on a 0–100 scale, used only when no payment exists yet. */
  readonly defaultBonusPercent = signal<number | null>(null);

  readonly saved = output<void>();
  readonly cancelled = output<void>();

  readonly saving = signal(false);
  readonly errorMessage = signal<string | null>(null);

  readonly isApproved = computed(() => this.payment()?.approved === true);

  /** Same gate the inline Delete button carried: an existing row that approval has not locked. */
  readonly canDelete = computed(() => !!this.payment()?.id && !this.isApproved());

  // Cancel first, then Delete ahead of the primary — the ordering the rest of the
  // groundworks-modal-footer call sites use. An approved bonus is read-only, so it keeps Cancel
  // alone: the locked note in the body says why there is nothing else to press.
  readonly footerActions = computed<ModalFooterAction[]>(() => {
    const actions: ModalFooterAction[] = [
      { label: 'Cancel', type: 'outline-secondary', action: () => this.cancel() },
    ];

    if (this.canDelete()) {
      actions.push({
        label: 'Delete',
        type: 'outline-danger',
        action: () => this.delete(),
        disabled: () => this.saving(),
      });
    }

    if (!this.isApproved()) {
      actions.push({
        label: 'Save Bonus',
        type: 'primary',
        action: () => this.submit(),
        loading: () => this.saving(),
        disabled: () => this.form.invalid || this.saving(),
      });
    }

    return actions;
  });

  readonly form = this.formBuilder.group({
    // Both signed since 2026-08-12: a negative pot yields negative shares, and a negative amount
    // claws back an overpayment. They must lose min(0) together — bonusPercent.valueChanges writes
    // into bonusAmount whatever the percent control's own validity, so a guard on one alone would
    // let the form save a figure it was simultaneously flagging. `required` stays on the amount and
    // is now the only gate on it: a CLEARED field is null and still blocked, while a typed 0 is a
    // present value that the server records (MOD-4753).
    bonusPercent: this.formBuilder.control<number | null>(null),
    bonusAmount: this.formBuilder.control<number | null>(null, [Validators.required, maxTwoDecimalPlaces]),
    eligibleDate: this.formBuilder.control<string | null>(null, Validators.required),
    notes: this.formBuilder.control<string | null>(null),
  });

  private readonly amountInput = viewChild<ElementRef<HTMLInputElement>>('amountInput');

  /**
   * Set only while the percent handler is writing the amount. The amount handler writes the percent
   * back with `emitEvent: false`, so the ONLY loop to break is percent → amount → percent, and that
   * one has to be broken by a flag rather than by silencing the amount write: the amount emits so
   * that `formatAmount()` runs against a freshly derived figure.
   */
  private updatingFromPercent = false;

  constructor() {
    effect(() => {
      const existing = this.payment();
      // Keyed on the payment ROW existing, never on its amount — same rule as
      // PpjEmployeeBreakdownBuilder.BuildRow, and for the same reason: a recorded $0.00 payment is a
      // real row and must not be treated as "no payment yet" and have the default re-applied over it.
      // `emitEvent: false`: the reset already computes BOTH sides of the percent/amount pair, so
      // letting it emit would only send those values back through the sync handlers to be re-derived
      // from each other — and against a zero or null pot the round trip erases the prefilled percent
      // (percentFromDollarsOfBasis has no answer there) instead of leaving it standing.
      this.form.reset({
        bonusPercent: existing == null
          ? this.defaultBonusPercent()
          : percentFromDollarsOfBasis(existing.amount ?? null, this.bonusPot()),
        bonusAmount: existing?.amount
          ?? dollarsFromPercentOfBasis(this.defaultBonusPercent(), this.bonusPot()),
        eligibleDate: existing?.dateQualified ? formatUtcIsoDate(existing.dateQualified) : null,
        notes: existing?.notes ?? null,
      }, { emitEvent: false });

      if (existing?.approved === true) {
        this.form.disable({ emitEvent: false });
      } else {
        this.form.enable({ emitEvent: false });
      }
    });

    this.form.controls.bonusPercent.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((percent) => {
        const dollars = dollarsFromPercentOfBasis(percent, this.bonusPot());
        if (dollars === null) {
          return;
        }
        this.updatingFromPercent = true;
        this.form.controls.bonusAmount.setValue(dollars);
        this.updatingFromPercent = false;
        this.formatAmount();
      });

    this.form.controls.bonusAmount.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((amount) => this.syncPercentFromAmount(amount));
  }

  private syncPercentFromAmount(amount: number | null): void {
    if (this.updatingFromPercent) {
      return;
    }

    // A cleared amount is a cleared share — the one case that legitimately blanks the percent.
    if (amount == null) {
      this.form.controls.bonusPercent.setValue(null, { emitEvent: false });
      return;
    }

    const percent = percentFromDollarsOfBasis(amount, this.bonusPot());
    if (percent === null) {
      // Zero or absent pot: there is nothing to express the dollars as a share OF. Leave whatever
      // percent is on screen standing rather than blanking it with a division that never happened.
      return;
    }

    this.form.controls.bonusPercent.setValue(percent, { emitEvent: false });
  }

  /**
   * Rewrites the amount box to two decimals on blur. It touches the INPUT ELEMENT, not the control:
   * the control already holds the rounded number, and re-setting it would bounce the value back
   * through the sync handlers.
   */
  formatAmount(): void {
    const control = this.form.controls.bonusAmount;
    const input = this.amountInput()?.nativeElement;
    if (input == null || control.invalid || control.value == null) {
      return;
    }
    input.value = control.value.toFixed(2);
  }

  submit(): void {
    if (this.form.invalid) {
      return;
    }
    this.errorMessage.set(null);
    this.saving.set(true);

    const value = this.form.getRawValue();
    const existing = this.payment();
    // `?? 0` on amount is unreachable, and must stay that way: `Validators.required` on
    // bonusAmount blocks submit while it is null, and an approved payment's form is disabled with
    // Save withheld. The API accepts a $0 payout (MOD-4753), so a template change that let a null
    // through here would silently record one instead of failing loudly at the server.
    const request$ = existing?.id
      ? this.ppjService.updatePayment(existing.id, new UpdatePpjPaymentDto({
          amount: value.bonusAmount ?? 0,
          dateQualified: parseLocalIsoDate(value.eligibleDate!),
          payDate: existing.payDate,
          notes: value.notes ?? undefined,
        }))
      : this.ppjService.recordPayment(this.projectId(), new RecordPpjPaymentDto({
          userId: this.userId(),
          amount: value.bonusAmount ?? 0,
          dateQualified: parseLocalIsoDate(value.eligibleDate!),
          notes: value.notes ?? undefined,
        }));

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.saving.set(false);
        this.saved.emit();
      },
      error: (err: unknown) => {
        this.saving.set(false);
        this.errorMessage.set(commissionErrorMessage(err));
      },
    });
  }

  delete(): void {
    const existing = this.payment();
    if (!existing?.id || existing.approved === true) {
      return;
    }
    this.errorMessage.set(null);
    this.saving.set(true);
    this.ppjService.deletePayment(existing.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.saved.emit();
        },
        error: (err: unknown) => {
          this.saving.set(false);
          this.errorMessage.set(commissionErrorMessage(err));
        },
      });
  }

  cancel(): void {
    this.cancelled.emit();
  }
}
